const ACTIONS = Object.freeze({
  "thread:new": { icon: "post", verb: "posted in" },
  "reply:new": { icon: "reply", verb: "replied in" },
  "like:new": { icon: "like", verb: "liked your" },
  "hub:member-request": { icon: "membership", verb: "requested to join" },
  "hub:member-approved": { icon: "membership", verb: "approved your membership in" },
  "hub:member-rejected": { icon: "membership", verb: "declined your membership in" },
  "hub:member-removed": { icon: "membership", verb: "removed you from" },
});

export const NOTIFICATION_VIEWS = Object.freeze([
  { key: "unread", label: "Unread" },
  { key: "read", label: "Read" },
  { key: "all", label: "All" },
]);

export function notificationTimestamp(value) {
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (value instanceof Date) return value.getTime();
  if (typeof value === "number") return value;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

export function relativeNotificationTime(value, now = Date.now()) {
  const timestamp = notificationTimestamp(value);
  if (!timestamp) return "now";
  const seconds = Math.max(0, Math.floor((now - timestamp) / 1000));
  if (seconds < 60) return "now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d`;
  return new Date(timestamp).toLocaleDateString();
}

export function notificationActor(notification = {}) {
  return String(notification.authorDisplayName || notification.authorHandle || "Someone").replace(/^@+/, "");
}

export function notificationAction(notification = {}) {
  return ACTIONS[notification.type] || { icon: "post", verb: "updated" };
}

export function notificationSpaceName(notification = {}) {
  const explicitName = String(notification.hubName || "").trim();
  if (explicitName) return explicitName;
  const slug = String(notification.hubSlug || "").trim();
  if (!slug) return "this space";
  return slug
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

export function notificationContentLabel(notification = {}) {
  const kinds = Array.isArray(notification.mediaKinds)
    ? notification.mediaKinds
    : notification.mediaKind
    ? [notification.mediaKind]
    : [];
  const labels = [];
  if (kinds.includes("image")) labels.push("an image");
  if (kinds.includes("video")) labels.push("a video");
  if (kinds.includes("link")) labels.push("a link");
  if (labels.length === 0) return "";
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(", ")}, and ${labels.at(-1)}`;
}

export function notificationTitle(notification = {}) {
  const actor = notificationActor(notification);
  const action = notificationAction(notification);
  const space = notificationSpaceName(notification);

  if (notification.type === "like:new") {
    return `${actor} ${action.verb} ${notification.targetType || "post"}`;
  }
  if (notification.type === "hub:member-request") {
    return `${actor} ${action.verb} ${space}`;
  }
  if (notification.type?.startsWith("hub:member-")) {
    return `${actor} ${action.verb} ${space}`;
  }
  const content = notificationContentLabel(notification);
  if (notification.type === "thread:new") {
    return `${actor} posted${content ? ` ${content}` : ""} in ${space}`;
  }
  if (notification.type === "reply:new") {
    return `${actor} replied${content ? ` with ${content}` : ""} in ${space}`;
  }
  return `${actor} ${action.verb} ${space}`;
}

export function notificationMediaLine(notification = {}) {
  const kinds = Array.isArray(notification.mediaKinds)
    ? notification.mediaKinds
    : notification.mediaKind
    ? [notification.mediaKind]
    : [];
  const hasImage = kinds.includes("image");
  const hasVideo = kinds.includes("video");
  const hasLink = kinds.includes("link");
  if (hasImage && hasVideo) return "Posted an image and a video";
  if (hasVideo) return "Posted a video";
  if (hasImage) return "Posted an image";
  if (hasLink) return "Posted a link";
  return "";
}

export function filterNotifications(items = [], view = "all") {
  const filtered = items.filter((item) => {
    if (view === "unread") return !item.read;
    if (view === "read") return !!item.read;
    return true;
  });
  return filtered.slice().sort((a, b) => notificationTimestamp(b.createdAt) - notificationTimestamp(a.createdAt));
}
